import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FONT_FAMILY_MAP } from '../utils';
import { useTranslation } from 'react-i18next';

// Register components for Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

const needlePlugin = {
  id: 'needle',
  afterDatasetsDraw(chart) {
    const {
      ctx,
      options,
      chartArea: { left, right },
    } = chart;
    // console.log(data);
    // if (config.options.needleValue === undefined) return;

    const dataValue = options.needleValue ?? 0;
    const dataMax = 100;
    // console.log(dataValue, dataMax);
    // Calculate angle: Map the value to the semi-circle (180 degrees)
    // For dialChart (semi-circle), we start at -90 deg (Math.PI) to 90 deg (0)
    const angle = Math.PI + (1 / dataMax) * dataValue * Math.PI;

    const cx = (left + right) / 2;
    const cy = chart.getDatasetMeta(0).data[0].y; // Center point of the arcs
    const outerRadius = chart.getDatasetMeta(0).data[0].outerRadius;

    ctx.save();

    // Draw Needle
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, -5); // Width of needle base
    ctx.lineTo(outerRadius, 0); // Tip of needle
    ctx.lineTo(0, 5); // Width of needle base
    ctx.fillStyle = '#444';
    ctx.fill();

    // Draw Dot at the center
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  },
};

// const dialScalePlugin = ({
//   min = 0,
//   max = 100,
//   step = 5,
//   tickLength = 8,
//   labelOffset = 14,
//   fontSize = 10,
//   fontFamily = 'Arial',
//   color = '#666',
// } = {}) => ({
//   id: 'dialScale',
//   afterDatasetsDraw(chart) {
//     const { ctx, chartArea } = chart;
//     const meta = chart.getDatasetMeta(0);
//     if (!meta?.data?.length) return;

//     const arc = meta.data[0];
//     const cx = (chartArea.left + chartArea.right) / 2;
//     const cy = arc.y;
//     const radius = arc.outerRadius;

//     ctx.save();
//     ctx.translate(cx, cy);
//     ctx.font = `${fontSize}px ${fontFamily}`;
//     ctx.fillStyle = color;
//     ctx.strokeStyle = color;
//     ctx.textAlign = 'center';
//     ctx.textBaseline = 'middle';

//     for (let value = min; value <= max; value += step) {
//       const ratio = (value - min) / (max - min);
//       const angle = Math.PI + ratio * Math.PI;

//       ctx.save();
//       ctx.rotate(angle);

//       // Tick
//       ctx.beginPath();
//       ctx.moveTo(radius - tickLength, 0);
//       ctx.lineTo(radius, 0);
//       ctx.stroke();

//       // Label
//       ctx.translate(radius + labelOffset, 0);
//       ctx.rotate(-angle); // keep text horizontal
//       ctx.fillText(value.toString(), 0, 0);

//       ctx.restore();
//     }

//     ctx.restore();
//   },
// });

const DialChartComponent = ({ widget, stats }) => {
  const { t } = useTranslation();

  const { advancedProps = {} } = widget;

  const valueFontFamily = FONT_FAMILY_MAP[advancedProps.formattingFontWeight] || '';

  const COLORS = ['rgb(255 0 0)', 'rgb(255 255 0)', 'rgb(0 128 0)'];
  const MAX_VALUE = 100;

  const rawValue = stats?.count ?? 0;
  const clampedValue = Math.min(Math.max(rawValue, 0), MAX_VALUE);

  // 1. Prepare Data
  const chartData = useMemo(
    () => ({
      datasets: [
        {
          data: [60, 25, 15],
          backgroundColor: COLORS,
          borderWidth: 1,
          // disable hover effect
          hoverBackgroundColor: COLORS,
        },
      ],
    }),
    [clampedValue],
  );

  // 2. Prepare Options
  const options = {
    needleValue: clampedValue,
    responsive: true,
    maintainAspectRatio: false,
    rotation: -90,
    circumference: 180,
    cutout: '60%',
    layout: {
      padding: {
        top: 20,
        right: 30,
        left: 20,
        bottom: 10,
      },
    },
    plugins: {
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false,
      },
    },
  };

  return !stats ? (
    <div>{t('text_loading_chart_data')}</div>
  ) : (
    <div className='d-flex flex-column align-items-center p-3'>
      <div className='d-flex flex-grow-1 w-100'>
        <Pie
          data={chartData}
          options={options}
          plugins={[
            needlePlugin,
            // dialScalePlugin({
            //   min: 0,
            //   max: 100,
            //   step: 5,
            //   fontSize: 12,
            //   color: '#444',
            // }),
          ]}
        />
      </div>
      <div
        className='p-1 text-black theme-background-white-1 rounded-sm'
        style={{
          fontFamily: valueFontFamily,
          fontSize: advancedProps.formattingFontSize,
        }}
      >
        value: {stats.count}
      </div>
    </div>
  );
};

DialChartComponent.propTypes = {
  widget: PropTypes.object.isRequired,
  stats: PropTypes.object,
  chartType: PropTypes.string.isRequired,
};

export default DialChartComponent;
